type QueryInputParams = Record<string, string | number | null>;
type QueryParams = Record<string, string | null>;

/**
 * クエリ文字列操作の実装を分離
 * 同キーが複数存在するクエリは考慮対象外
 * なにがしかのライブラリを導入したほうが良いように思われる
 */
export default class QueryString {
    static queryToObject(query: string): QueryParams {
        return Object.fromEntries(
            (query[0] === '?' ? query.slice(1) : query)
                .split('&')
                .map((param) => {
                    const [key, rawValue] = param.split('=');
                    const value = rawValue
                        ? decodeURIComponent(rawValue.replace(/\+/g, '%20'))
                        : (rawValue ?? null);
                    return [key, value];
                })
        );
    }

    static objectToQuery(params: QueryInputParams) {
        return (
            '?' +
            Object.entries(params)
                .map(([key, value]) =>
                    value === null ? key : `${key}=${encodeURIComponent(value)}`
                )
                .join('&')
        );
    }

    static current(): QueryString {
        return new this(location.search);
    }

    static parse(query: string): QueryString {
        return new this(query);
    }

    static create(params: QueryInputParams): QueryString {
        return new this(params);
    }

    private readonly _params: QueryParams;
    get params(): Readonly<QueryParams> {
        return { ...this._params };
    }

    private constructor(query: string | QueryInputParams) {
        this._params =
            typeof query === 'string'
                ? QueryString.queryToObject(query)
                : this.parseParams(query);
    }

    private parseParams(params: QueryInputParams): QueryParams {
        return Object.fromEntries(
            Object.entries(params)
                .filter(
                    ([, value]) =>
                        value === null ||
                        typeof value === 'string' ||
                        typeof value === 'number'
                )
                .map(([key, value]) => [
                    key,
                    value === null ? null : String(value),
                ])
        );
    }

    toString(): string {
        return QueryString.objectToQuery(this._params);
    }

    toJSON() {
        return this._params;
    }

    patch(params: QueryInputParams): QueryString {
        return new QueryString({ ...this._params, ...params });
    }

    remove(...keys: string[]): QueryString {
        const entries = Object.entries(this._params).filter(
            ([key]) => !keys.includes(key)
        );
        return new QueryString(Object.fromEntries(entries));
    }

    asInt(key: string): number | null {
        return parseInt(this._params[key] ?? '') || null;
    }
}
